import {Box, Grid, Typography} from '@mui/material';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {t} from 'i18next';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import AurButton from '../../common/aur-button/AurButton';
import AurDataGridPro, {
  CustomGridColDef,
} from '../../common/aur-data-grid-pro/AurDataGridPro';
import AurPagination from '../../common/aur-pagination/AurPagination';
import FieldPopoverGridFilter, {
  FieldFilterGridData,
} from '../field-management/field-table/FieldPopoverGridFilter';

import {getUTCDateTimeForDisplay, unwrapQueryText} from '../../../utils/utils';
import {
  getGridDateOperators,
  GridRenderHeaderFilterProps,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import FilterTextField from '../../common/aur-data-grid-pro/FilterTextField';
import AurDataGridCellWrapper from '../../common/aur-data-grid/AurDataGridCellWrapper';
import {useGridApiRef} from '@mui/x-data-grid-pro';
import {TABLE_PAGE_SIZE_OPTION} from '../../../constants/constants';
import {getSearchAuditReport} from '../../../api/EcaManagementService';
import {
  getFormatedDateForFilter,
  toODataQueryResult,
} from '../../common/aur-data-grid-pro/useServerSideDataGrid';
import AurDrawer from '../../common/aur-drawer/AurDrawer';
import AurIcon, {IconNames} from '../../common/aur-icon/AurIcon';
import AurInfographicSeperator from '../../common/aur-infographic/AurInfographicSeperator';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import AurDataGridLoadingOverlay from '../../common/aur-data-grid-loading-overlay/AurDataGridLoadingOverlay';
import AurDataGridNoRowsOverlay from '../../common/aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';
import {debounce} from 'lodash';
import {useRelationGroups} from './useRelationGroups';
import {getFieldCategoryList} from '../../../utils/fieldManagementUtils';

//const TABLE_BASE_WIDTH = 1328;
interface SearchRow {
  id: number;
  searchId: string;
  name: string;
  relationshipGroups: string;
  query: string;
  scope: string;
  type: string;
  filters: string;
  createdTime: string;
  createdUserId: string;
  createdUserName: string;
  updatedTime: string;
  updatedUserId: string;
  updatedUserName: string;
  lastRunTime: string;
  lastRunUserId: string;
  lastRunUserName: string;
  status: string;
  groupName: string;
}

const SavedSearchReport = () => {
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [totalItems, setTotalItems] = useState(100);
  const [itemsPerPage, setItemsPerPage] = useState(TABLE_PAGE_SIZE_OPTION[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterCount, setFilterCount] = useState<number>(0);
  const [dataContent, setDataContent] = useState<any>({});
  const [showDrawer, setShowDrawer] = useState(false);
  const {projectId} = useSelector((state: RootState) => state.reports);
  const {clientId} = useSelector((state: RootState) => state.selectedClient);
  const [reportData, setSearchData] = useState<SearchRow[]>([]);
  //const clientName = useSelector((state: RootState) => state.selectedClient);
  const [loading, setLoading] = useState(false);
  const stringPath = 'eca.savedSearchSelectionDialog.';
  const initialLoad = useRef(false);
  const lastODataQuery = useRef<string | null>(null);
  const apiRef = useGridApiRef();
  const {relationalGroups} = useRelationGroups();

  const fetchSearchReport = useCallback(
    async (ODataQuery: string) => {
      if (lastODataQuery.current === ODataQuery) return;
      lastODataQuery.current = ODataQuery;
      setLoading(true);
      try {
        const paramString = `clientId=${clientId}&projectId=${projectId}&PageNumber=${currentPage}&PageSize=${itemsPerPage}&${ODataQuery}`;
        const response = await getSearchAuditReport(paramString);
        const res = response.data;
        const data = res?.data || [];

        setTotalItems(res?.meta?.pagination?.totalItems);
        const rowsWithId = data.map((row: any, index: any) => ({
          ...row,
          id: index + 1,
        }));
        setSearchData(rowsWithId);
      } catch (error) {
        console.error('Error fetching audit report:', error);
      } finally {
        setLoading(false);
      }
    },
    [clientId, currentPage, itemsPerPage, projectId]
  );

  const handleRowClick = async (data: any) => {
    setShowDrawer(true);
    setDataContent(data);
  };

  const fieldTypeSearchScope: FieldFilterGridData[] = [
    {id: 1, label: t('common.all')},
    {id: 2, label: t('ecaHomepageModals.tabsRightPanel.savedSearch.searchId')},
    {id: 3, label: t('ecaHomepageModals.tabsLeftPanel.tabLabels.loads')},
    {id: 4, label: t('ecaHomepageModals.tabsLeftPanel.tabLabels.tags')},
  ];

  const fieldTypeStatus: FieldFilterGridData[] = [
    {id: 1, label: t('common.statusTypes.queued')},
    {id: 2, label: t('common.statusTypes.inProgress')},
    {id: 3, label: t('common.statusTypes.completed')},
    {id: 4, label: t('common.statusTypes.failed')},
  ];

  const renderEmptyHeaderFilter = (label: string) => (
    <span className="sr-only">{label}</span>
  );

  const columns: CustomGridColDef[] = [
    {
      field: 'searchId',
      headerName: t(stringPath + 'searchId'),
      width: 200,
      sortable: true,
      resizable: false,
      renderCell: (params) => (
        <AurDataGridCellWrapper cellData={params.value} />
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
        return (
          <FilterTextField
            gridRenderHeaderFilterProps={params}
            label={t('common.search')}
          />
        );
      },
    },
    {
      field: 'name',
      headerName: t(stringPath + 'searchName'),
      width: 200,
      sortable: true,
      resizable: false,
      renderCell: (params) => (
        <AurDataGridCellWrapper cellData={params.value} />
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
        return (
          <FilterTextField gridRenderHeaderFilterProps={params} label="name" />
        );
      },
    },
    {
      field: 'groupName',
      headerName: t(stringPath + 'searchGroup'),
      width: 200,
      sortable: true,
      resizable: false,
      renderCell: (params) => (
        <AurDataGridCellWrapper cellData={params.value} />
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
        return (
          <FilterTextField
            gridRenderHeaderFilterProps={params}
            label={t('common.search')}
          />
        );
      },
    },
    {
      field: 'query',
      headerName: t(stringPath + 'query'),
      width: 224,
      sortable: false,
      resizable: false,
      renderCell: (params) => (
        <AurDataGridCellWrapper cellData={params.value} />
      ),
      renderHeaderFilter: () =>
        renderEmptyHeaderFilter(
          `${t(stringPath + 'query') + t('common.filterNotAvailable')}`
        ),
    },
    {
      field: 'scope',
      headerName: t(stringPath + 'searchScope'),
      width: 200,
      sortable: true,
      resizable: false,
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          key={'scope'}
          apiRef={apiRef}
          columnField={'scope'}
          type={'options'}
          dataArray={fieldTypeSearchScope}
        />
      ),
    },
    {
      field: 'relationshipGroups',
      headerName: t(stringPath + 'relationship'),
      width: 224,
      sortable: true,
      resizable: false,
      renderHeaderFilter: () =>
        renderEmptyHeaderFilter(
          `${t(stringPath + 'relationship') + t('common.filterNotAvailable')}`
        ),
    },
    {
      field: 'filters',
      headerName: t(stringPath + 'filters'),
      width: 224,
      sortable: true,
      resizable: false,
      renderCell: (params) => (
        <AurDataGridCellWrapper cellData={params.value} />
      ),
      renderHeaderFilter: () => null,
    },
    {
      field: 'numberOfDocs',
      headerName: t(stringPath + 'numberOfDocsLast'),
      width: 230,
      sortable: true,
      resizable: false,
      renderHeaderFilter: () => null,
    },
    {
      field: 'createdUserName',
      headerName: t(stringPath + 'createdBy'),
      width: 200,
      sortable: true,
      resizable: false,
      renderCell: (params) => (
        <AurDataGridCellWrapper cellData={params.value} />
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
        return (
          <FilterTextField
            gridRenderHeaderFilterProps={params}
            label={t('common.search')}
          />
        );
      },
    },
    {
      field: 'createdTime',
      headerName: t(stringPath + 'createdDate'),
      width: 240,
      sortable: false,
      resizable: false,
      type: 'date',
      filterOperators: getGridDateOperators().map((operator) => ({
        ...operator,
      })),
      isDateColumn: true,
      valueGetter: (value) => {
        return new Date(value);
      },
      valueFormatter: (_value, row) => {
        return getUTCDateTimeForDisplay(row.createdTime);
      },
      renderCell: (param) => {
        return (
          <AurDataGridCellWrapper
            tooltipText={t('common.yearMonthDate')}
            cellData={param.formattedValue}
          />
        );
      },
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          apiRef={apiRef}
          columnField={'createdTime'}
          type={'date'}
          dataArray={getFieldCategoryList()}
        />
      ),
    },
    {
      field: 'updatedUserName',
      headerName: t(stringPath + 'lastModifiedBy'),
      width: 200,
      sortable: true,
      resizable: false,
      renderCell: (params) => (
        <AurDataGridCellWrapper cellData={params.value} />
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
        return (
          <FilterTextField
            gridRenderHeaderFilterProps={params}
            label="Search"
          />
        );
      },
    },
    {
      field: 'lastRunUserName',
      headerName: t(stringPath + 'lastRunBy'),
      width: 200,
      sortable: true,
      resizable: false,
      renderCell: (params) => (
        <AurDataGridCellWrapper cellData={params.value} />
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
        return (
          <FilterTextField
            gridRenderHeaderFilterProps={params}
            label={t('common.search')}
          />
        );
      },
    },
    {
      field: 'lastRunTime',
      headerName: t(stringPath + 'lastRunTime'),
      width: 280,
      sortable: true,
      resizable: false,
      type: 'date',
      filterOperators: getGridDateOperators().map((operator) => ({
        ...operator,
      })),
      isDateColumn: true,
      valueGetter: (value) => {
        return new Date(value);
      },
      valueFormatter: (_value, row) => {
        return getUTCDateTimeForDisplay(row.lastRunTime);
      },
      renderCell: (param) => {
        return (
          <AurDataGridCellWrapper
            tooltipText={t('common.yearMonthDate')}
            cellData={param.formattedValue}
          />
        );
      },
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          apiRef={apiRef}
          columnField={'lastRunTime'}
          type={'date'}
          dataArray={getFieldCategoryList()}
        />
      ),
    },
    {
      field: 'status',
      headerName: t(stringPath + 'status'),
      width: 200,
      sortable: false,
      resizable: false,
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          key={'status'}
          apiRef={apiRef}
          columnField={'status'}
          type={'options'}
          dataArray={fieldTypeStatus}
        />
      ),
    },
    {
      field: 'arrow',
      headerName: '',
      width: 24,
      sortable: false,
      filterable: false,
      headerClassName: 'no-separator',
      disableTooltip: true,
      renderCell: (params: {row: any}) => (
        <div
          tabIndex={0}
          onClick={(event) => {
            event.stopPropagation();
            handleRowClick(params?.row);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              handleRowClick(params?.row);
            }
          }}
        >
          <img
            style={{cursor: 'pointer'}}
            src="/assests/images/chev_right.svg"
            alt="Show Query"
          />
        </div>
      ),
    },
  ];

  const fetchData = useCallback(() => {
    if (!apiRef?.current || !projectId || !clientId) return;
    const pageName = 'SavedSearch';
    const filterModel = apiRef.current.state.filter.filterModel;
    const sortModel = apiRef.current.state.sorting.sortModel;
    const uniqueFields = new Set(
      filterModel?.items?.map((item) => item.field) || []
    );
    setFilterCount(uniqueFields.size);
    const updatedGridFilterModel = filterModel
      ? {
          ...filterModel,
          items: filterModel?.items.map((item: any) => {
            return {
              ...item,
              field: item.field.charAt(0).toUpperCase() + item.field.slice(1),
              value:
                item.field === 'lastRunTime' || item.field === 'createdTime'
                  ? getFormatedDateForFilter(item.value)
                  : item.value,
            };
          }),
        }
      : filterModel;
    const updatedSortModel = sortModel.map((item: any) => {
      return {
        ...item,
        field: item.field.charAt(0).toUpperCase() + item.field.slice(1),
      };
    });
    const skipColumnNames = [
      'CreatedUserName',
      'UpdatedUserName',
      'LastRunUserName',
      'GroupName',
    ];
    const ODataQuery = toODataQueryResult(
      updatedGridFilterModel,
      updatedSortModel,
      {page: currentPage, pageSize: itemsPerPage},
      skipColumnNames,
      pageName
    );

    fetchSearchReport(ODataQuery);
  }, [
    apiRef,
    projectId,
    clientId,
    currentPage,
    itemsPerPage,
    fetchSearchReport,
  ]);

  useEffect(() => {
    if (!initialLoad.current) {
      initialLoad.current = true;
    } else {
      lastODataQuery.current = null;
    }
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!apiRef?.current) return;

    const debouncedFetchData = debounce(() => {
      fetchData();
    }, 1000);

    const handleFilterChange = () => {
      if (currentPage === 1) {
        debouncedFetchData();
      } else {
        setCurrentPage(1);
      }
    };
    const unsubscribe = apiRef.current.subscribeEvent(
      'filterModelChange',
      handleFilterChange
    );

    const unsubscribeSort = apiRef.current.subscribeEvent(
      'sortModelChange',
      handleFilterChange
    );

    return () => {
      unsubscribe();
      unsubscribeSort();
      debouncedFetchData.cancel();
    };
  }, [apiRef, fetchData]);

  const onPageChange = (page: any) => {
    setCurrentPage(page);
  };
  const handlePageSizeChange = (pageSize: number) => {
    setItemsPerPage(pageSize);
    setCurrentPage(1);
  };

  const onSetSortModel = (sortModel: GridSortModel) => {
    setCurrentPage(1);
    setSortModel(sortModel);
  };

  const contentBody = (
    <Box className="manage-field-sort-panel">
      <Box className="panel-header">
        <Box className="close-btn-container">
          <AurButton
            classNames={'drawer-close-button panel-close'}
            label={t('buttons.close')}
            type="exit"
            size="Medium"
            title={t('buttons.close')}
            onClick={() => setShowDrawer(false)}
          />
        </Box>
        <Grid
          container
          spacing={2}
          justifyContent="space-between"
          className="export-area"
        >
          <Grid item xs={8}>
            <Typography className="typo-text subhead4_size query_preview">
              {t(stringPath + 'queryPreview')}
            </Typography>
            <Typography className="typo-text headline1_size query_preview_head">
              {dataContent?.name}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Box className="manageColSort-content">
        <Box className="tab-container">
          <Typography className="typo-text body2_size query_preview_content">
            {unwrapQueryText(dataContent?.query)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const renderPaginationBlock = (position: 'top' | 'bottom') => {
    return (
      <Box className={'table-pagination-container'}>
        <Box className={'table-pagination-control'}>
          <AurPagination
            totalItems={totalItems}
            itemType={t('fieldManagement.fieldTable.events')}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            perPageOptions={TABLE_PAGE_SIZE_OPTION}
            onPageChange={onPageChange}
            setItemsPerPage={handlePageSizeChange}
            paginationAriaLabel={
              position === 'top'
                ? t('common.pagination.topPagination', {
                    tableName: t(stringPath + 'savedSearches'),
                  })
                : t('common.pagination.bottomPagination', {
                    tableName: t(stringPath + 'savedSearches'),
                  })
            }
          />
        </Box>
        {filterCount > 0 ? (
          <>
            <Box>
              <AurInfographicSeperator seperatorIcon={'VerticalLine'} />
            </Box>
            <Box
              className={'apply-filter-button-container'}
              onClick={() => {
                apiRef.current?.setFilterModel({items: []});
              }}
            >
              <AurButton
                type={'exit'}
                size={'Small'}
                startIcon={<AurIcon icon={IconNames.ClearFiltersIcon} />}
                label={`${t('fieldManagement.fieldTable.clearFilters')} (${filterCount})`}
              />
            </Box>
          </>
        ) : null}
      </Box>
    );
  };

  const noDataGridOverlay = useMemo(() => {
    if (loading) return AurDataGridLoadingOverlay;
    if (filterCount > 0 && !(reportData.length > 0))
      return AurDataGridNoRowsOverlay;
    return AurDataGridNoRowsOverlay;
  }, [
    filterCount,
    loading,
    reportData.length,
  ]) as React.JSXElementConstructor<unknown>;

  const rowsData = useMemo(() => {
    return reportData.map((data: any) => {
      const relationshipGroupNames = data?.relationshipGroups
        ?.split(',')
        .map(
          (id?: string) =>
            relationalGroups.find((item) => item.id.toString() === id?.trim())
              ?.name
        )
        ?.join(', ');
      return {
        ...data,
        relationshipGroups: relationshipGroupNames,
      };
    });
  }, [relationalGroups, reportData]);

  return (
    <>
      <AurDrawer
        className="report-drawer"
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        anchor="right"
        drawerContent={contentBody}
      />
      <div className="tag-report-screen">
        <Box className={'aur-field-table-container'}>
          <div className="eca-searchterm-report tagging-report-screen">
            <div className="eca-searchterm-report-table">
              <div className="data-table-container">
                <div className="total-content">
                  <Typography className="typo-text subhead2_size" tabIndex={0}>
                    {t('tagging-audit-report.totalEvents')}
                  </Typography>
                  <Typography className="count-text" tabIndex={0}>
                    ({totalItems})
                  </Typography>
                </div>
                {renderPaginationBlock('top')}
                <AurDataGridPro
                  rows={rowsData}
                  columns={columns}
                  className="aur-grid-pro"
                  apiRef={apiRef}
                  disableRowSelectionOnClick
                  headerFilters
                  hideFooter
                  columnHeaderHeight={48}
                  headerFilterHeight={48}
                  rowHeight={36}
                  sortModel={sortModel}
                  setSortModel={onSetSortModel}
                  filterDebounceMs={500}
                  slots={{
                    noRowsOverlay: noDataGridOverlay,
                  }}
                  filterMode="server"
                  enableServerSide={true}
                  noRowOverlayClass="no-row-container"
                  data-testid="str-table"
                  checkBoxVisible={false}
                  defaultItemsPerPage={itemsPerPage}
                  isReqLoading={loading}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                />
                {renderPaginationBlock('bottom')}
              </div>
            </div>
          </div>
          <AurLoadingIndicator overlay={true} isOpen={loading} />
        </Box>
      </div>
    </>
  );
};

export default SavedSearchReport;
